/**
 * Creates a horizontally scrollable content area with navigation arrows and dynamic height adjustment.
 */
export class HorizontalScroller {
  /**
   * @param {Object} options - Configuration options
   * @param {NodeList} options.elements - NodeList of scroller elements
   * @param {boolean} [options.autoHeight=false] - Whether to automatically adjust the height of the viewport
   */
  constructor(options = {}) {
    this.options = {
      autoHeight: false,
      ...options,
    };
    this.scrollers = options.elements;
    this.activeScroller = null;
    this.init();
  }

  /**
   * Initialize the horizontal scroller
   * Sets up all necessary components and event listeners
   */
  init() {
    this.scrollers.forEach((scroller) => {
      const viewport = scroller.querySelector(".horizontal-scroller__viewport");
      const prevArrow = scroller.querySelector(
        ".horizontal-scroller__arrow--prev"
      );
      const nextArrow = scroller.querySelector(
        ".horizontal-scroller__arrow--next"
      );
      const toggle = scroller.querySelector(".horizontal-scroller__toggle");
      const items = scroller.querySelectorAll(".horizontal-scroller__item");

      if (!viewport) {
        console.error(
          "Viewport not found in scroller: .horizontal-scroller__viewport"
        );
        return;
      }

      this.observeViewport(scroller, viewport);
      this.setupItemObserver(scroller, viewport, items);
      this.setupItemClickListeners(items, toggle);
      this.setupArrowNavigation(scroller, prevArrow, nextArrow, items);

      if (this.options.autoHeight) {
        this.setAutoHeight(scroller, viewport, items);
      }

      const firstItem = scroller.querySelector(".horizontal-scroller__item");
      if (firstItem) {
        firstItem.classList.add("horizontal-scroller__item--active");
      }
    });
  }

  /**
   * Set up auto-height adjustment for the scroller
   * @param {Element} scroller - The main scroller element
   * @param {Element} viewport - The viewport element
   * @param {NodeList} items - The scrollable items
   */
  setAutoHeight(scroller, viewport, items) {
    const updateHeight = () => {
      const activeItem = scroller.querySelector(
        ".horizontal-scroller__item--active"
      );
      if (activeItem) {
        // Set the height on the scroller (parent) element
        scroller.style.maxHeight = `${activeItem.offsetHeight}px`;
        // Ensure the viewport takes up the full height of the scroller
        viewport.style.maxHeight = "100%";
      }
    };

    // Initial height set
    updateHeight();

    // Update height on window resize
    window.addEventListener("resize", updateHeight);

    // Update height when active item changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          updateHeight();
        }
      });
    });

    items.forEach((item) => {
      observer.observe(item, { attributes: true });
    });

    // Update height when images load
    items.forEach((item) => {
      const images = item.querySelectorAll("img");
      images.forEach((img) => {
        if (img.complete) {
          updateHeight();
        } else {
          img.addEventListener("load", updateHeight);
        }
      });
    });
  }

  /**
   * Observe viewport for size changes
   * @param {Element} scroller - The main scroller element
   * @param {Element} viewport - The viewport element
   */
  observeViewport(scroller, viewport) {
    if (!(viewport instanceof Element)) {
      console.error("Invalid viewport element:", viewport);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        scroller.classList.toggle(
          "horizontal-scroller--scrollable",
          entry.target.scrollWidth > entry.target.clientWidth
        );
      });
    });

    try {
      observer.observe(viewport);
    } catch (error) {
      console.error("Failed to observe viewport:", error);
    }
  }

  /**
   * Set up intersection observer for items
   * @param {Element} scroller - The main scroller element
   * @param {Element} viewport - The viewport element
   * @param {NodeList} items - The scrollable items
   */
  setupItemObserver(scroller, viewport, items) {
    if (!viewport || items.length === 0) {
      console.error("Invalid viewport or no items found");
      return;
    }

    const itemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activeScroller = scroller;
            entry.target.classList.add("horizontal-scroller__item--active");
          } else {
            entry.target.classList.remove("horizontal-scroller__item--active");
          }
        });
      },
      { root: viewport, threshold: 1.0, rootMargin: "100% 0% 100% 0%" }
    );

    items.forEach((item) => {
      if (item instanceof Element) {
        itemObserver.observe(item);
      } else {
        console.error("Invalid item element:", item);
      }
    });
  }

  /**
   * Set up click listeners for items
   * @param {NodeList} items - The scrollable items
   * @param {Element|null} toggle - The toggle element, if any
   */
  setupItemClickListeners(items, toggle) {
    items.forEach((item) => {
      if (item instanceof Element) {
        item.addEventListener("click", () => {
          if (toggle && toggle instanceof Element) {
            toggle.click();
          }
        });
      }
    });
  }

  /**
   * Set up navigation arrows
   * @param {Element} scroller - The main scroller element
   * @param {Element|null} prevArrow - The previous arrow element
   * @param {Element|null} nextArrow - The next arrow element
   * @param {NodeList} items - The scrollable items
   */
  setupArrowNavigation(scroller, prevArrow, nextArrow, items) {
    const scrollToItem = (item) => {
      if (item && item instanceof Element) {
        item.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    };

    const getActiveItems = () =>
      scroller.querySelectorAll(".horizontal-scroller__item--active");

    const setupArrow = (arrow, getTargetItem) => {
      if (arrow instanceof Element) {
        arrow.addEventListener("click", () => {
          const activeItems = getActiveItems();
          const targetItem = getTargetItem(activeItems);
          scrollToItem(targetItem);
        });
      }
    };

    setupArrow(prevArrow, (activeItems) => {
      const firstActive = activeItems[0];
      return firstActive?.previousElementSibling || items[items.length - 1];
    });

    setupArrow(nextArrow, (activeItems) => {
      const lastActive = activeItems[activeItems.length - 1];
      return lastActive?.nextElementSibling || items[0];
    });
  }
}
